import React, { useEffect, useState } from 'react';
import IModuleList from '../../utils/ModuleList/ModuleList.interface';
import { IPreset } from '../../utils/ModulePresets/preset.interface';
import IUserList from '../../utils/UserList/UserList.interface';
import PartnerAdminRow from '../PartnerAdminRow';
import Pagination from '../../library/Pagination/Pagination';
import Search from '../../components/Search';
import ConditionalLoader from '../../components/ConditionalLoader';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { changePartner, deletePartner, getAllAdmins, switchAdmin } from '../../store/slice/partnersSlice';
import { getAllModules } from '../../store/slice/moduleSlice';
import { getAllPresets } from '../../store/slice/presetSlice';
import SpotlightSearchModal from '../../components/SpotlightSearch/SpotlightSearchModal';
import FloatingTooltip from '../../components/FloatingToolTip/FloatingTooltip';
import { useNavigate } from 'react-router-dom';
import { btAdminRoutes } from '../../routes/Routes';
import { isMac } from '../../utils/Agent/Agent';
import { useModuleFuse, usePartnerFuse, usePresetFuse } from '../../hooks/Fuses/useFuse';

const PartnerAdmin: React.FC = () => {
  const dispatch = useAppDispatch();
  const moduleState = useAppSelector((state) => state.modules);
  const presetState = useAppSelector((state) => state.presets);
  const partnerState = useAppSelector((state) => state.partners);
  const partners = partnerState.data ?? [];
  const presets = presetState.data ?? [];
  const modules = moduleState.data ?? [];
  const isLoading = !partnerState.data && partnerState.status === 'loading';
  const [query, setQuery] = useState<string>('');
  const [systemModuleName, setSystemModuleName] = useState<Array<IModuleList>>([]);
  const [baseModules, setBaseModules] = useState<Array<IModuleList>>([]);
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [highlightedPartnerEmail, setHighlightedPartnerEmail] = useState<string | null>(null);
  const [highlightedPresets, setHighlightedPresets] = useState<string | null>(null);
  const [highlightedModules, setHighlightedModules] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const screenHeight = window.innerHeight;
  // based on screenHeight display items
  const itemsPerPage = screenHeight ? Math.ceil((screenHeight - 210) / 90) : 10;
  const sortedData = [...partners].sort((a, b) => {
    const vala = a.companyName ?? '';
    const valb = b.companyName ?? '';
    return vala.localeCompare(valb);
  });
  const filteredPartner = sortedData.filter((user) =>
    user.companyName?.toLowerCase().includes(query.toLowerCase())
  );
  const showPagination = !query;
  const navigate = useNavigate();
  // Calculate the index of the first and last items to display on the current page
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDataPartnerAdmin = filteredPartner.slice(indexOfFirstItem, indexOfLastItem);

  const resetPageAndSearch = (newQuery: string) => {
    setQuery(newQuery);
    handlePageChange(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  // Handle Partner edit locally
  const handlePartnerChange = (partner: IUserList) => {
    dispatch(changePartner(partner));
  };
  // Handle admin switch — old email needed since the key changes
  const handleSwitchAdmin = (oldEmail: string, updatedPartner: IUserList) => {
    dispatch(switchAdmin({ oldEmail, updatedPartner }));
  };
  // Handle Partner delete locally
  const handlePartnerDelete = (partner: IUserList) => {
    dispatch(deletePartner(partner));
  };

  const partnerFuse = usePartnerFuse(sortedData);
  const presetFuse = usePresetFuse(presets);
  const moduleFuse = useModuleFuse(modules);

  const performFuzzySearch = (searchTerm: string) => {
    if (!searchTerm) {
      setHighlightedPartnerEmail(null);
      setHighlightedPresets(null);
      setHighlightedModules(null);
      setCurrentPage(1);
      return;
    }

    const partnerResults = partnerFuse.search(searchTerm);
    const presetResults = presetFuse.search(searchTerm);
    const moduleResults = moduleFuse.search(searchTerm);

    if (partnerResults.length > 0) {
      const firstMatch = partnerResults[0].item;
      const indexInSorted = sortedData.findIndex((partner) => partner.email === firstMatch.email);
      if (indexInSorted !== -1) {
        const page = Math.floor(indexInSorted / itemsPerPage) + 1;
        setCurrentPage(page);
        setTimeout(() => {
          setHighlightedPartnerEmail(firstMatch.email!);
        }, 0);
        setTimeout(() => setQuery(''), 100);
        return;
      }
    }

    if (presetResults.length > 0) {
      const firstPreset = presetResults[0].item;
      void navigate(btAdminRoutes.presets, { state: { highlightPreset: firstPreset.presetName } });
      setShowSpotlight(false);
      return;
    }

    if (moduleResults.length > 0) {
      const firstModule = moduleResults[0].item;
      const sortedModules = [...modules].sort((a, b) => a.moduleName.localeCompare(b.moduleName));
      const indexInSortedModules = sortedModules.findIndex(
        (mod) => mod.moduleName === firstModule.moduleName
      );

      if (indexInSortedModules !== -1) {
        const page = Math.floor(indexInSortedModules / itemsPerPage) + 1;
        setCurrentPage(page);
        setTimeout(() => {
          setHighlightedModules(firstModule.moduleName);
        }, 0);
        setHighlightedPartnerEmail(null);
        setHighlightedPresets(null);
        setTimeout(() => setQuery(''), 100);
        return;
      }
    }

    setHighlightedPartnerEmail(null);
    setHighlightedPresets(null);
    setHighlightedModules(null);
  };

  const handleSelectPartner = (partner: IUserList) => {
    performFuzzySearch(partner.companyName!);
    setHighlightedPartnerEmail(partner.email!);

    setTimeout(() => {
      const row = document.getElementById(`partner-row-${partner.email}`);
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  };

  const handleSelectPreset = (preset: IPreset) => {
    void navigate(btAdminRoutes.presets, { state: { highlightPreset: preset.presetName } });
    setShowSpotlight(false);
  };

  const handleSelectModule = (module: IModuleList) => {
    void navigate(btAdminRoutes.modules, { state: { highlightModule: module.moduleName } });
    setShowSpotlight(false);
  };

  useEffect(() => {
    if (partnerState.status === 'loading' || !partnerState.data) {
      void dispatch(getAllAdmins());
    }
    if (moduleState.status === 'loading' || !moduleState.data) {
      void dispatch(getAllModules());
    }
    if (presetState.status === 'loading' || !presetState.data) {
      void dispatch(getAllPresets());
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (cmdOrCtrl && e.shiftKey) {
        e.preventDefault();
        setShowSpotlight((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <ConditionalLoader isLoading={isLoading} className="flex justify-center">
      <div className="w-full md:w-full">
        <div className="bg-white block md:w-[100%] mx-auto md:ps-0.5 md:pe-0.5">
          <div className="py-2">
            <Search
              onChange={(e: any) => resetPageAndSearch(e.target.value)}
              text="Search by partner..."
              hideLabel
            />
          </div>
          <div>
            {filteredPartner.length > 0 ? (
              <div className="max-w-full overflow-hidden">
                <div className="max-w-full overflow-scroll min-h-[500px]">
                  <div className="min-w-fit border rounded-md overflow-hidden">
                    <table className="min-w-fit w-full whitespace-nowrap rounded-lg bg-white divide-y divide-gray-300">
                      <thead className="bg-[#f8f8f8] pr-6 lg:pr-auto">
                        <tr className="text-black text-center">
                          <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                            {' '}
                            Partner Name{' '}
                          </th>
                          <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                            {' '}
                            Admin User Name{' '}
                          </th>

                          <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                            {' '}
                            No. of Users{' '}
                          </th>
                          <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                            {' '}
                            Assigned Modules{' '}
                          </th>
                          <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                            {' '}
                            Actions{' '}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {currentDataPartnerAdmin.map((dataSet: IUserList, index: number) => (
                          <PartnerAdminRow
                            key={dataSet.key}
                            id={`partner-row-${dataSet.email}`}
                            partner={dataSet}
                            highlightedPartnerEmail={highlightedPartnerEmail}
                            onChangePartner={handlePartnerChange}
                            onSwitchAdmin={handleSwitchAdmin}
                            onDeletePartner={handlePartnerDelete}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {showPagination && filteredPartner.length > itemsPerPage && (
                  <Pagination
                    className={'pagination-bar mt-4 flex justify-center items-center m-auto'}
                    currentPage={currentPage}
                    totalCount={filteredPartner.length}
                    pageSize={itemsPerPage}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            ) : (
              <div className="text-center font-bold pt-20 px-6 pb-4">
                <h1 className="font-bold">No data found ☹️ !!</h1>
              </div>
            )}
            <FloatingTooltip />
            <SpotlightSearchModal
              isOpen={showSpotlight}
              onClose={() => setShowSpotlight(false)}
              partnerList={partners}
              onSelect={handleSelectPartner}
              presets={presets}
              onSelectPreset={handleSelectPreset}
              modules={modules}
              onSelectModules={handleSelectModule}
            />
          </div>
        </div>
      </div>
    </ConditionalLoader>
  );
};

export default PartnerAdmin;
